import { Card, OutlinedInput } from "@material-ui/core";
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import ReactQuill from "react-quill";
import IOSSwitch from "./IOSSwitch";
import DefaultTooltip from "./DefaultTooltip";
import ClientIdListEditor from "./ClientIdListEditor";
import RetrieveSourceFieldList from "./RetrieveSourceFieldList";
import { buildRetrieveUrlSources } from "./retrieveUrlSources";
import CorporationRetrieveStatusAlert from "./CorporationRetrieveStatusAlert";
import ConfirmDialog from "./ConfirmDialog";
import ThemeEditorModal from "./ThemeEditorModal";
import { LOGIN_METHOD_WITH_SUB_FORM_TYPES, isHardcodedLoginCheckbox } from "./loginMethodRules";

const URL_LINK_BUTTON_STYLE = {
    margin: '5px',
    background: 'black',
    borderRadius: '4px',
    color: 'white',
    width: 'fit-content',
    height: '25px',
    marginBottom: '5px',
    padding: '5px',
    border: '1px solid',
    fontSize: '16px',
    lineHeight: '10px',
};

const URL_LINK_BUTTON_COLORS = {
    Facebook: { borderColor: '#1877F2', backgroundColor: '#dfebf9', color: '#1877F2' },
    Google: { borderColor: '#ff0000', color: '#ff0000', backgroundColor: '#ffd9d9' },
    Azure: { borderColor: '#04BAF6', color: '#04BAF6', backgroundColor: '#e5f8ff' },
    OneID: { borderColor: '#ff9900', color: '#ff9900', backgroundColor: '#ffefd8' },
    OneCollab: { borderColor: '#0d00ff', color: '#0d00ff', backgroundColor: '#e2e3ff' },
    MophDID: {  borderColor: '#3d7655', color: '#3d7655', backgroundColor: '#f2fff7' },
    ThaID: {  borderColor: '#030c50', color: '#030c50', backgroundColor: '#f2f3ff', },
};

// จุดครอบ margin เดียวสำหรับทั้งบล็อกฟอร์ม (form name/type, อนุมัติ, login method, retrieve)
// แทนที่จะเซ็ต marginLeft ซ้ำในแต่ละ row เอง — แก้ทีเดียวถ้า spacing เปลี่ยน
const FORM_SECTION_STYLE = { marginLeft: '50px' };

/**
 * JSX ที่เหมือนกันเป๊ะระหว่าง CreatePortal.jsx และ EditPortal.jsx ทั้งหมด รวมมาไว้ที่นี่
 * ที่เดียว ไฟล์ wrapper (CreatePortal/EditPortal) เหลือแค่ดึง Redux, useEffect เฉพาะโหมด,
 * ฟังก์ชัน submit (addPortal/editPortal) แล้วเรียก component นี้พร้อม props + footer
 * (ปุ่ม Cancel/Save ด้านล่างสุด ไม่รวมมาที่นี่ เพราะ layout ต่างกันจริงระหว่างสองไฟล์)
 *
 * แก้ inconsistency ที่เจอระหว่างรวมไฟล์:
 * - ผูก checked ให้ checkbox ทุกตัวที่เดิม CreatePortal.jsx ไม่ได้ผูกไว้ (uncontrolled)
 * - แก้บั๊ก OneCollab uncheck ไปลบ retrieve ผิดตัว (เดิมใน CreatePortal.jsx)
 * - ผูก value ให้ Select ประเภทฟอร์ม (เดิม CreatePortal.jsx ไม่ได้ผูก)
 * - รวม marginLeft: '50px' ที่เดิมซ้ำอยู่ทุก row (form name/type, อนุมัติ, login method, retrieve)
 *   ให้เหลือ style เดียวที่ wrapper `col` แทน (ดู FORM_SECTION_STYLE ด้านบน) และย้าย
 *   ConfirmDialog/ThemeEditorModal ออกไปนอก wrapper นั้น เพราะเป็น modal ไม่เกี่ยวกับ indent
 */
const PortalFormBody = ({
    mode, // 'create' | 'edit' — ปัจจุบันไม่ได้ใช้ตรง ๆ ใน component นี้ (ควบคุมผ่าน props ย่อยแทน) เก็บไว้เผื่อ debug/log
    primaryColor,

    active,
    onRequestToggleActive,

    formName,
    onFormNameChange,

    onOpenTheme,

    formType,
    onFormTypeChange,
    formTypeDisabled,

    useCA,
    onToggleUseCA,

    autoApprove,
    approvalMethodDisabled,
    onSetAutoApprove,
    clientIDList,
    setClientIDList,

    loginMethod,
    onLoginMethodChange,
    idp,
    retrieveUrlSetters, // { Azure, Google, Facebook, OneCollab } -> setter ล้างค่า URL ตอน uncheck login method

    retrieve,
    setRetrieve,
    retrieveFieldValues, // { link, setLink, token, setToken, azureurl, setAzureURL, googleurl, setGoogleURL, facebookurl, setFacebookURL }

    redirectUrl,
    onRedirectUrlChange,
    clientId,
    onClientIdChange,

    showUpdateRealTime,
    updateRealTime,
    onToggleUpdateRealTime,

    contact,
    onContactChange,

    urlLinkConfig, // null (Create) หรือ { items: [{name, url}], isCopied, onCopy } (Edit)

    cancelPortalDialogProps,
    acceptPortalDialogProps,
    switchActiveDialogProps,
    cancelThemeDialogProps,
    themeModalProps,

    children, // footer: ปุ่ม Cancel/Save ด้านล่างสุด ต่างกันจริงระหว่าง Create/Edit จึงให้ wrapper render เอง
}) => {
    const safeLoginMethod = Array.isArray(loginMethod) ? loginMethod : [];
    const retrieveUrlSources = buildRetrieveUrlSources(retrieveFieldValues);
    const showRetrieveSection = safeLoginMethod.length !== 0;
    const showUseCASwitch =
        formType === "edu-student-pre-register" ||
        formType === "edu-teacher-pre-register" ||
        formType === "edu-officer-pre-register";

    const handleIdpCheckboxChange = (data) => (e) => {
        if (e.target.checked) {
            onLoginMethodChange([...safeLoginMethod, { name: data.name }]);
            return;
        }
        onLoginMethodChange(safeLoginMethod.filter((obj) => obj.name !== data.name));
        const clearUrl = retrieveUrlSetters[data.name];
        if (clearUrl) {
            setRetrieve(retrieve.filter((obj) => obj.from !== data.name));
            clearUrl('');
        }
    };

    return (
        <>
            <div className="row my-3">
                <div className="col">
                    <h3 style={{ color: primaryColor, fontWeight: 'bold' }}>จัดการหน้าลงทะเบียนคนเข้า Business</h3>
                </div>
            </div>
            <Card style={{ borderRadius: '20px' }}>
                <div className="row m-2">
                    <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
                        <p style={{ fontWeight: 'bold', marginLeft: '30px', marginTop: '30px' }}>รายละเอียดฟอร์ม</p>
                    </div>
                    <div className="col">
                        <div style={{ display: 'flex', justifyContent: 'right', marginRight: '40px' }}>
                            <p
                                style={{
                                    border: '2px #e4e4e4 solid',
                                    borderRadius: '10px',
                                    paddingLeft: '18px',
                                    fontWeight: 'bold',
                                    marginTop: '20px',
                                    paddingRight: '15px',
                                }}
                            >
                                สถานะ
                                <FormControlLabel
                                    style={{ width: '48px', height: '52px', marginLeft: '5px' }}
                                    control={<IOSSwitch sx={{ m: 1 }} />}
                                    onChange={onRequestToggleActive}
                                    checked={active}
                                />
                                {active ? <>Active</> : <>Inactive</>}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col" style={FORM_SECTION_STYLE}>
                        <div className="row">
                            <div className="col-xs-12 col-md-12 col-lg-12 col-xl-4">
                                <p>ชื่อฟอร์ม</p>
                                <OutlinedInput
                                    style={{
                                        width: '80%',
                                        maxWidth: '352px',
                                        height: '36px',
                                        borderRadius: '6px',
                                        marginTop: '4px',
                                        marginBottom: '15px',
                                    }}
                                    onChange={(e) => {
                                        if (e.target.value.match(/^[!@#$%^&*()_\-,.\/ ]/)) {
                                            onFormNameChange('');
                                        } else {
                                            onFormNameChange(e.target.value);
                                        }
                                    }}
                                    value={formName}
                                    placeholder="ระบุชื่อฟอร์ม"
                                />
                                {formName === '' ? (
                                    <Alert severity="error">กรุณากรอกชื่อ</Alert>
                                ) : (
                                    <Alert severity="success">กรุณากรอกชื่อ</Alert>
                                )}
                                <p style={{ fontWeight: 'bold', marginTop: 15 }}>กำหนดธีม</p>
                                <Button
                                    onClick={onOpenTheme}
                                    style={{
                                        fontSize: '16px',
                                        color: 'black',
                                        border: 'none',
                                        boxShadow: 'none',
                                        width: '150px',
                                        height: '40px',
                                        marginBottom: '15px',
                                    }}
                                >
                                    ธีมเริ่มต้น
                                    <FormatColorFillIcon style={{ marginLeft: '10px', color: primaryColor }} />
                                </Button>
                            </div>
                            <div className="col-xs-12 col-md-12 col-lg-12 col-xl-4">
                                <p>ประเภทฟอร์ม</p>
                                <FormControl style={{ width: '80%', maxWidth: '352px' }}>
                                    <InputLabel labelId="เลือกประเภทฟอร์ม">เลือกประเภทฟอร์ม</InputLabel>
                                    <Select
                                        value={formType}
                                        labelId="เลือกประเภทฟอร์ม"
                                        label="เลือกประเภทฟอร์ม"
                                        disabled={formTypeDisabled}
                                        style={{ height: '40px', borderRadius: '6px' }}
                                        onChange={(e) => onFormTypeChange(e.target.value)}
                                    >
                                        <MenuItem value="general">ทั่วไป</MenuItem>
                                        <MenuItem value="general-lms">ทั่วไป(lms)</MenuItem>
                                        <MenuItem value="edu-student-pre-register">นักศึกษา</MenuItem>
                                        <MenuItem value="edu-student-pre-register-combine">นักศึกษา(combine)</MenuItem>
                                        <MenuItem value="edu-teacher-pre-register">อาจารย์</MenuItem>
                                        <MenuItem value="edu-officer-pre-register">บุคลากร</MenuItem>
                                        <MenuItem value="medical-pre-register">บุคลากรทางการแพทย์</MenuItem>
                                        <MenuItem value="time-attendance-pre-register">ระบบเช็คชื่อนักเรียน</MenuItem>
                                        <MenuItem value="corporation-pre-register">สมาชิก</MenuItem>
                                        <MenuItem value="connect-mail">ทั่วไป(เชื่อมอีเมล)</MenuItem>
                                        <MenuItem value="corporation-mail">ทั่วไป(อีเมลองค์กร)</MenuItem>
                                        <MenuItem value="general-mail">อีเมล</MenuItem>
                                    </Select>
                                </FormControl>
                                {formType === '' ? (
                                    <Alert style={{ marginBottom: '15px', marginTop: '15px' }} severity="error">
                                        กรุณาเลือกประเภทฟอร์ม
                                    </Alert>
                                ) : (
                                    <Alert style={{ marginBottom: '15px', marginTop: '15px' }} severity="success">
                                        กรุณาเลือกประเภทฟอร์ม
                                    </Alert>
                                )}
                                {showUseCASwitch && (
                                    <>
                                        <div style={{ fontWeight: 'bold' }}>ใช้งานใบรับรองอิเล็กทรอนิกส์</div>
                                        <FormControlLabel
                                            style={{ width: '48px', height: '52px', marginLeft: '5px' }}
                                            control={<IOSSwitch sx={{ m: 1 }} />}
                                            onChange={onToggleUseCA}
                                            checked={useCA}
                                        />
                                        {useCA ? <>ใช้งาน</> : <>ไม่ใช้งาน</>}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="row">
                            <p style={{ fontWeight: 'bold' }}>วิธีอนุมัติคนเข้าองค์กร</p>
                            <RadioGroup
                                row
                                disabled={approvalMethodDisabled}
                                name="RadioGroupAddtoBiz"
                                defaultValue="เพิ่มคนเข้าองค์กรอัตโนมัติ"
                            >
                                <FormControlLabel
                                    disabled={approvalMethodDisabled}
                                    style={{ marginRight: '15px' }}
                                    value="เพิ่มคนเข้าองค์กรอัตโนมัติ"
                                    onChange={() => onSetAutoApprove(true)}
                                    control={<Radio style={{ color: primaryColor }} />}
                                    label="เพิ่มคนเข้าองค์กรอัตโนมัติ"
                                />
                            </RadioGroup>
                            {autoApprove === false && (
                                <>
                                    <p style={{ marginTop: '15px' }}>Client ID</p>
                                    <ClientIdListEditor
                                        clientIDList={clientIDList}
                                        setClientIDList={setClientIDList}
                                        primaryColor={primaryColor}
                                    />
                                </>
                            )}
                        </div>

                        <div className="row">
                            <p style={{ fontWeight: 'bold', marginTop: '30px' }}>วิธีการเข้าสู่ระบบ</p>
                            <RadioGroup aria-labelledby="demo-row-radio-buttons-group-label" name="RadioGroupLogin">
                                {formType === "general-lms" ? (
                                    <FormControlLabel
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                onLoginMethodChange([...safeLoginMethod, { name: "ThaID", url: null }]);
                                            } else {
                                                onLoginMethodChange(safeLoginMethod.filter((obj) => obj.name !== "ThaID"));
                                            }
                                        }}
                                        control={
                                            <Checkbox
                                                style={{ color: primaryColor }}
                                                checked={safeLoginMethod.some((obj) => obj.name === "ThaID")}
                                            />
                                        }
                                        label="ThaID"
                                    />
                                ) : formType === "corporation-mail" ? (
                                    <FormControlLabel
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                onLoginMethodChange([...safeLoginMethod, { name: "Zimbra", url: null }]);
                                            } else {
                                                onLoginMethodChange(safeLoginMethod.filter((obj) => obj.name !== "Zimbra"));
                                            }
                                        }}
                                        control={
                                            <Checkbox
                                                style={{ color: primaryColor }}
                                                checked={safeLoginMethod.some((obj) => obj.name === "Zimbra")}
                                            />
                                        }
                                        label="Zimbra"
                                    />
                                ) : LOGIN_METHOD_WITH_SUB_FORM_TYPES.includes(formType) ? (
                                    <FormControlLabel
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                onLoginMethodChange([...safeLoginMethod, { name: "OneID", url: null }]);
                                            } else {
                                                onLoginMethodChange(safeLoginMethod.filter((obj) => obj.name !== "OneID"));
                                            }
                                        }}
                                        control={
                                            <Checkbox
                                                style={{ color: primaryColor }}
                                                checked={safeLoginMethod.some((obj) => obj.name === "OneID")}
                                            />
                                        }
                                        label="OneID"
                                    />
                                ) : null}
                                {formType !== "corporation-mail" &&
                                    idp.filter((data) => !isHardcodedLoginCheckbox(data.name, formType))
                                        .map((data) => (
                                            <FormControlLabel
                                                key={data.name}
                                                onChange={handleIdpCheckboxChange(data)}
                                                control={
                                                    <Checkbox
                                                        style={{ color: primaryColor }}
                                                        checked={safeLoginMethod.some((obj) => obj.name === data.name)}
                                                    />
                                                }
                                                label={data.name}
                                            />
                                        ))}
                            </RadioGroup>
                            {safeLoginMethod.length === 0 ? (
                                <Alert severity="error">
                                    กรุณาเลือกวิธีการเข้าสู่ระบบ
                                </Alert>
                            ) : (
                                <Alert severity="success">
                                    กรุณาเลือกวิธีการเข้าสู่ระบบ
                                </Alert>
                            )}
                        </div>

                        {showRetrieveSection && (
                            <div className="row">
                                <p style={{ fontWeight: 'bold', marginTop: '30px' }}>วิธีการดึงข้อมูลส่วนบุคคลในองค์กร</p>
                                <RadioGroup aria-labelledby="demo-col-radio-buttons-group-label" name="RadioGroupLogin">
                                    <RetrieveSourceFieldList
                                        sources={retrieveUrlSources.filter((s) => s.key === 'API')}
                                        loginMethod={safeLoginMethod}
                                        retrieve={retrieve}
                                        setRetrieve={setRetrieve}
                                        primaryColor={primaryColor}
                                    />
                                    <FormControlLabel
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setRetrieve([...retrieve, { from: "OneID", url: null, token: null, corporation_Id: "corporation_Id" }]);
                                            } else {
                                                setRetrieve(retrieve.filter((obj) => obj.from !== "OneID"));
                                            }
                                        }}
                                        control={
                                            <Checkbox
                                                style={{ color: primaryColor }}
                                                checked={retrieve.some((obj) => obj.from === "OneID")}
                                                disabled={formType === "corporation-pre-register"}
                                            />
                                        }
                                        label="ดึงข้อมูลจากฐานข้อมูลที่ทางองค์กรนำเข้าระบบ UM"
                                    />
                                    {Array.isArray(retrieve) &&
                                        retrieve.find((item) => item.from === "OneID") &&
                                        formType === "corporation-pre-register" && (
                                            <>
                                                <FormControlLabel
                                                    style={{ marginLeft: '20px' }}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setRetrieve(
                                                                retrieve.map((item) =>
                                                                    item.from === "OneID" ? { ...item, cid: 'cid' } : item
                                                                )
                                                            );
                                                        } else {
                                                            setRetrieve(
                                                                retrieve.map((item) => {
                                                                    if (item.from === "OneID") {
                                                                        const { cid, ...rest } = item;
                                                                        return rest;
                                                                    }
                                                                    return item;
                                                                })
                                                            );
                                                        }
                                                    }}
                                                    control={
                                                        <Checkbox
                                                            style={{ color: primaryColor }}
                                                            checked={retrieve.some((obj) => obj.from === "OneID" && obj.cid != null)}
                                                        />
                                                    }
                                                    label="ดึงข้อมูลจากเลขบัตรประชาชน"
                                                />
                                                <FormControlLabel
                                                    style={{ marginLeft: '20px' }}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setRetrieve(
                                                                retrieve.map((item) =>
                                                                    item.from === "OneID" ? { ...item, email: 'email' } : item
                                                                )
                                                            );
                                                        } else {
                                                            setRetrieve(
                                                                retrieve.map((item) => {
                                                                    if (item.from === "OneID") {
                                                                        const { email, ...rest } = item;
                                                                        return rest;
                                                                    }
                                                                    return item;
                                                                })
                                                            );
                                                        }
                                                    }}
                                                    control={
                                                        <Checkbox
                                                            style={{ color: primaryColor }}
                                                            checked={retrieve.some((obj) => obj.from === "OneID" && obj.email != null)}
                                                        />
                                                    }
                                                    label="ดึงข้อมูลจากอีเมล"
                                                />
                                                <FormControlLabel
                                                    style={{ marginLeft: '20px' }}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setRetrieve(
                                                                retrieve.map((item) =>
                                                                    item.from === "OneID"
                                                                        ? { ...item, corporation_Id: 'corporation_Id' }
                                                                        : item
                                                                )
                                                            );
                                                        } else {
                                                            setRetrieve(
                                                                retrieve.map((item) => {
                                                                    if (item.from === "OneID") {
                                                                        const { corporation_Id, ...rest } = item;
                                                                        return rest;
                                                                    }
                                                                    return item;
                                                                })
                                                            );
                                                        }
                                                    }}
                                                    control={
                                                        <Checkbox
                                                            style={{ color: primaryColor }}
                                                            checked={retrieve.some((obj) => obj.from === "OneID" && obj.corporation_Id != null)}
                                                        />
                                                    }
                                                    label="ดึงข้อมูลจากเลขสมาชิก"
                                                />
                                                <CorporationRetrieveStatusAlert retrieve={retrieve} />
                                            </>
                                        )}

                                    <RetrieveSourceFieldList
                                        sources={retrieveUrlSources.filter((s) => s.key !== 'API')}
                                        loginMethod={safeLoginMethod}
                                        retrieve={retrieve}
                                        setRetrieve={setRetrieve}
                                        primaryColor={primaryColor}
                                    />
                                </RadioGroup>
                                {retrieve.length === 0 ? (
                                    <Alert severity="error">
                                        กรุณาเลือกวิธีการดึงข้อมูลส่วนบุคคลในองค์กร
                                    </Alert>
                                ) : (
                                    <Alert severity="success">
                                        กรุณาเลือกวิธีการดึงข้อมูลส่วนบุคคลในองค์กร
                                    </Alert>
                                )}
                            </div>
                        )}
                    </div>

                    <ConfirmDialog {...cancelPortalDialogProps} />
                    <ConfirmDialog {...acceptPortalDialogProps} />
                    <ConfirmDialog {...switchActiveDialogProps} />
                    <ConfirmDialog {...cancelThemeDialogProps} />
                    <ThemeEditorModal {...themeModalProps} />
                </div>

                <div className="row" style={{ marginLeft: '40px', marginTop: '30px' }}>
                    <p style={{ fontWeight: 'bold' }}>การตั้งค่าเพื่อสร้าง URL ในการลงทะเบียน</p>
                    <div>
                        <div>
                            redirect_URL{' '}
                            <DefaultTooltip title=" tooltip: การตั้งค่า URL เบื้องต้นสำหรับเข้าใช้งานหลังลงทะเบียน ตัวอย่าง https://box.one.th/login/bypass?sharetoken=xxx ">
                                <HelpOutlineIcon style={{ width: '20px', height: '20px', color: '#DADADA', marginLeft: '5px' }} />
                            </DefaultTooltip>
                        </div>
                        <OutlinedInput
                            value={redirectUrl}
                            style={{
                                width: '80%',
                                maxWidth: '360px',
                                height: '36px',
                                borderRadius: '6px',
                                marginRight: '10px',
                                marginTop: '15px',
                            }}
                            onChange={(e) => onRedirectUrlChange(e.target.value.startsWith(' ') ? '' : e.target.value)}
                            placeholder="redirect_URL-1234567890"
                        />
                        {redirectUrl === '' ? (
                            <Alert severity="error" style={{ marginTop: '5px' }}>กรุณากรอก redirect_URL</Alert>
                        ) : (
                            <Alert severity="success" style={{ marginTop: '5px' }}>กรุณากรอก redirect_URL</Alert>
                        )}
                    </div>
                    <div>
                        <div>
                            Client_ID{' '}
                            <DefaultTooltip title=" การตั้งค่า Client ID สำหรับระบุ Service Provider เบื้องต้นที่ใช้เพื่อเข้าลงทะเบียน">
                                <HelpOutlineIcon style={{ width: '20px', height: '20px', color: '#DADADA', marginLeft: '5px' }} />
                            </DefaultTooltip>
                        </div>
                        <OutlinedInput
                            value={clientId}
                            style={{
                                width: '80%',
                                maxWidth: '360px',
                                height: '36px',
                                borderRadius: '6px',
                                marginRight: '10px',
                                marginTop: '15px',
                            }}
                            onChange={(e) => onClientIdChange(e.target.value.replace(/\D/g, ''))}
                            placeholder="Client_ID-1234567890"
                        />
                        {clientId === '' ? (
                            <Alert severity="error" style={{ marginTop: '5px' }}>กรุณากรอก Client_ID</Alert>
                        ) : (
                            <Alert severity="success" style={{ marginTop: '5px' }}>กรุณากรอก Client_ID</Alert>
                        )}
                    </div>
                </div>

                {showUpdateRealTime && (
                    <div className="row" style={{ marginLeft: '40px', marginTop: '20px' }}>
                        <div className="col" style={{ height: '100%' }}>
                            <p style={{ fontWeight: 'bold' }}>อัปเดตข้อมูลข้อมูลส่วนบุคคลในองค์กร</p>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={updateRealTime}
                                        onChange={onToggleUpdateRealTime}
                                        style={{ color: primaryColor }}
                                    />
                                }
                                label="อัปเดตข้อมูล (กรณีเข้าสู่ระบบผ่าน Azure และต้องการอัปเดตข้อมูล)"
                            />
                        </div>
                    </div>
                )}

                <div className="row " style={{ marginLeft: '40px', height: '100%', marginTop: '20px', marginBottom: '90px' }}>
                    <div className="col" style={{ height: '100%' }}>
                        <p style={{ fontWeight: 'bold' }}>ตั้งค่าข้อความที่ต้องการแสดงกรณีหาข้อมูลไม่พบ</p>
                        <ReactQuill
                            style={{
                                height: '120px',
                                color: '#000',
                                background: '#fff',
                                width: '80%',
                                maxWidth: '360px',
                                marginRight: '10px',
                            }}
                            value={contact}
                            theme="snow"
                            onChange={onContactChange}
                        />
                    </div>
                </div>

                {urlLinkConfig && (
                    <div style={{ marginLeft: '50px', marginTop: '15px', marginBottom: '15px' }}>
                        <div style={{ marginBottom: '15px', fontWeight: 'bold' }}>URL Link</div>
                        {urlLinkConfig.items.map((item) => (
                            <DefaultTooltip
                                key={item.name}
                                title={urlLinkConfig.isCopied ? 'Copied' : item.url}
                                placement="top"
                            >
                                <button
                                    onClick={() => urlLinkConfig.onCopy(item.url)}
                                    style={{ ...URL_LINK_BUTTON_STYLE, ...(URL_LINK_BUTTON_COLORS[item.name] || {}) }}
                                >
                                    {item.name}
                                </button>
                            </DefaultTooltip>
                        ))}
                    </div>
                )}

                {children}
            </Card>
        </>
    );
};

export default PortalFormBody;